/**
 * Kaching bundles: carry the customer's variant choices across bundle bars.
 *
 * Each Kaching bar (Single / Duo / 3 Pairs / 4 Pairs) has its own rows. A
 * customer who picks a size in one bar and then clicks another bar sees
 * every row of that bar back at its default.
 *
 * When a customer presses on a different bar, this copies the options the
 * customer changed by hand (their size, color, flavor) from the outgoing
 * bar's rows into the incoming bar's rows, through the same dropdown changes
 * a customer would make. Options the customer never touched are left exactly
 * as Kaching set them, so per-bar defaults (for example a bag count that
 * differs per bar) are never overwritten. Rows the customer already set by
 * hand in the incoming bar are left alone. Extra rows inherit the last
 * copied row. It starts on finger-down / mouse-down, about 100ms before the
 * click lands, so the incoming bar is already correct when it appears; the
 * change handler at the end is the safety net for keyboard use. Only
 * Kaching's own bar rows are touched. If Kaching's markup changes, every
 * lookup below finds nothing and the script does nothing.
 */
(function () {
  'use strict';

  var BAR = '.kaching-bundles__bar';
  var ROW = '.kaching-bundles__bar-variant';
  var SELECT = 'select.kaching-bundles__bar-variant-select';
  var SWATCH = '.kaching-bundles__swatch-dropdown';
  var LIST = '.kaching-bundles__swatch-dropdown__options';
  var OPTION = '.kaching-bundles__swatch-dropdown__option';

  var touchedOptions = {};       // option positions the customer changed by hand (e.g. Size)
  var touched = new WeakSet();   // individual controls the customer changed by hand
  var applying = false;          // true while this script is making changes
  var queue = Promise.resolve(); // color menus are changed one at a time

  // Keep color menus invisible while this script opens and closes them.
  var style = document.createElement('style');
  style.textContent = 'html.kcv-silent ' + LIST + '{visibility:hidden!important}';
  document.head.appendChild(style);

  function controls(row) { return row.querySelectorAll(SELECT + ', ' + SWATCH); }
  function readValue(c) { return c.tagName === 'SELECT' ? c.value : c.getAttribute('data-value'); }
  function readBar(bar) {
    return Array.prototype.map.call(bar.querySelectorAll(ROW), function (row) {
      return Array.prototype.map.call(controls(row), readValue);
    });
  }
  function silently(fn) {
    applying = true;
    try { fn(); } finally { applying = false; }
  }
  function isOpen(trigger) { return trigger.getAttribute('aria-expanded') === 'true'; }
  function tick() { return new Promise(function (r) { Promise.resolve().then(r); }); }
  function later() { return new Promise(function (r) { setTimeout(r, 0); }); }

  // Remember which option (by position in the row) the customer changed.
  function markTouched(control) {
    var row = control.closest(ROW);
    if (!row) return;
    var index = Array.prototype.indexOf.call(controls(row), control);
    if (index < 0) return;
    touched.add(control);
    touchedOptions[index] = true;
  }

  function setSelect(sel, value) {
    if (sel.value === value) return;
    var exists = Array.prototype.some.call(sel.options, function (o) { return o.value === value; });
    if (!exists) return;
    silently(function () {
      sel.value = value;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function setSwatch(dropdown, value) {
    if (dropdown.getAttribute('data-value') === value) return;
    var trigger = dropdown.querySelector('[role="combobox"]');
    if (!trigger) return;
    function listEl() { return document.getElementById(trigger.getAttribute('aria-controls') || ''); }
    function finish() {
      if (isOpen(trigger)) silently(function () { trigger.click(); });
      document.documentElement.classList.remove('kcv-silent');
    }
    queue = queue.then(function () {
      if (dropdown.getAttribute('data-value') === value || isOpen(trigger)) return;
      document.documentElement.classList.add('kcv-silent');
      silently(function () { trigger.click(); });
      return tick().then(tick).then(function () {
        return listEl() || later().then(listEl);
      }).then(function (list) {
        var option = list ? Array.prototype.find.call(list.querySelectorAll(OPTION), function (o) {
          return o.textContent.trim() === value;
        }) : null;
        if (option) silently(function () { option.click(); });
        return tick().then(tick);
      }).then(finish, finish);
    });
  }

  function applyBar(bar, values) {
    if (!values.length) return;
    Array.prototype.forEach.call(bar.querySelectorAll(ROW), function (row, i) {
      var source = values[i] || values[values.length - 1];
      Array.prototype.forEach.call(controls(row), function (control, j) {
        if (!touchedOptions[j] || touched.has(control) || source[j] == null) return;
        if (control.tagName === 'SELECT') setSelect(control, source[j]);
        else setSwatch(control, source[j]);
      });
    });
  }

  function copyInto(to) {
    var root = to.closest('.kaching-bundles');
    var from = root && root.querySelector(BAR + '--selected');
    if (!from || from === to) return;
    applyBar(to, readBar(from));
  }

  // Dropdown changed by the customer.
  document.addEventListener('change', function (e) {
    if (applying) return;
    var t = e.target;
    if (t && t.matches && t.matches(SELECT)) markTouched(t);
  }, true);

  // Color menu option chosen by the customer.
  document.addEventListener('click', function (e) {
    if (applying) return;
    var option = e.target && e.target.closest ? e.target.closest(OPTION) : null;
    if (!option) return;
    var list = option.closest('[role="listbox"]');
    var trigger = list && list.id ? document.querySelector('[aria-controls="' + list.id + '"]') : null;
    var dropdown = trigger ? trigger.closest(SWATCH) : null;
    if (dropdown) markTouched(dropdown);
  }, true);

  // Press on another bar: copy early, before the click lands.
  document.addEventListener('pointerdown', function (e) {
    try {
      var to = e.target && e.target.closest ? e.target.closest(BAR) : null;
      if (to) copyInto(to);
    } catch (err) { /* never interfere with Kaching's own handling */ }
  }, true);

  // Bar switch (safety net, e.g. keyboard). Capture phase: runs before
  // Kaching's own handler switches the bar.
  document.addEventListener('change', function (e) {
    try {
      var input = e.target;
      if (!input || input.type !== 'radio' || !/^kaching-bundles-deal/.test(input.name || '')) return;
      var to = input.closest(BAR);
      if (to) copyInto(to);
    } catch (err) { /* never interfere with Kaching's own handling */ }
  }, true);
})();